import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import React, { ReactNode, useMemo, useState } from "react";
import { getTicketOrderKey } from "./ticketReorder";
import { GenericOrderPreview } from "./Ticket/types";

interface KdsTicketStripProps {
  orders: readonly GenericOrderPreview[];
  renderSummary: () => ReactNode;
  renderTicket: (order: GenericOrderPreview, index: number) => ReactNode;
  onReorder: (fromIndex: number, toIndex: number) => void;
  dragDisabled?: boolean;
}

const TICKET_WIDTH = "calc(100vw / 3 - 16px)";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      overflowX: "auto",
      overflowY: "hidden",
    },
    // Same layout model as the modal: flex children ARE the sortable nodes.
    // Do not wrap sortables in overflow:hidden — transforms would clip them away.
    row: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "stretch",
      height: "100%",
      width: "max-content",
      minWidth: "100%",
    },
    summarySlot: {
      flex: "0 0 auto",
      width: TICKET_WIDTH,
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
    },
    // Flex item + sortable target. Overflow only on an inner scroller so
    // horizontalListSortingStrategy can translate the whole slot into view.
    sortableSlot: {
      flex: "0 0 auto",
      width: TICKET_WIDTH,
      height: "100%",
      boxSizing: "border-box",
      position: "relative",
    },
    ticketScroll: {
      height: "100%",
      width: "100%",
      overflowY: "auto",
      overflowX: "hidden",
    },
    dragOverlay: {
      width: TICKET_WIDTH,
      cursor: "grabbing",
      pointerEvents: "none",
      zIndex: theme.zIndex.modal,
      opacity: 0.95,
    },
  })
);

interface SortableTicketSlotProps {
  id: string;
  disabled: boolean;
  children: ReactNode;
}

const SortableTicketSlot = ({ id, disabled, children }: SortableTicketSlotProps) => {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      className={classes.sortableSlot}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
        cursor: disabled ? undefined : isDragging ? "grabbing" : "grab",
        zIndex: isDragging ? 2 : undefined,
        // Allow horizontal strip scroll and ticket taps; drag still activates via PointerSensor distance.
        touchAction: "pan-x pan-y",
      }}
      {...attributes}
      {...(disabled ? {} : listeners)}
    >
      <div className={classes.ticketScroll}>{children}</div>
    </div>
  );
};

const KdsTicketStrip = ({ orders, renderSummary, renderTicket, onReorder, dragDisabled = false }: KdsTicketStripProps) => {
  const classes = useStyles();
  const sortableIds = useMemo(() => orders.map(getTicketOrderKey), [orders]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const activeOrderIndex = useMemo(() => {
    if (!activeId) {
      return -1;
    }
    return orders.findIndex(o => getTicketOrderKey(o) === activeId);
  }, [activeId, orders]);

  const activeOrder = activeOrderIndex >= 0 ? orders[activeOrderIndex] : null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    const fromIndex = orders.findIndex(o => getTicketOrderKey(o) === active.id);
    const toIndex = orders.findIndex(o => getTicketOrderKey(o) === over.id);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return;
    }
    onReorder(fromIndex, toIndex);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <div className={classes.root}>
      <DndContext
        sensors={dragDisabled ? [] : sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy} disabled={dragDisabled}>
          <div className={classes.row}>
            <div className={classes.summarySlot}>{renderSummary()}</div>
            {orders.map((order, index) => (
              <SortableTicketSlot key={getTicketOrderKey(order)} id={getTicketOrderKey(order)} disabled={dragDisabled}>
                {renderTicket(order, index)}
              </SortableTicketSlot>
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeOrder ? <div className={classes.dragOverlay}>{renderTicket(activeOrder, activeOrderIndex)}</div> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KdsTicketStrip;
